import type {
  AutoPlacementOptions,
  ComputePositionReturn,
  Coords,
  FloatingElement,
  Placement,
} from "@floating-ui/dom"
import type { Directive, DirectiveBinding } from "vue"
import {
  arrow,
  autoPlacement,
  computePosition,
  offset,
  shift,
} from "@floating-ui/dom"
import InfoIcon from "@kaleidoscope-solutions/ks-icons/optimized/light/Info.svg?raw"

function createTooltipElement(innerText: string) {
  const tooltipEl = document.createElement("div")
  tooltipEl.setAttribute("data-tooltip", "")
  tooltipEl.innerHTML = innerText.replace(/\r/g, "").replace(/\n/g, "<br>")
  tooltipEl.classList.add(
    "absolute",
    "bg-dark",
    "cursor-default",
    "flex",
    "gap-2",
    "hidden",
    "items-center",
    "max-w-md",
    "px-3",
    "py-1.5",
    "rounded-md",
    "font-light",
    "text-white",
    "z-50"
  )

  return tooltipEl
}

interface Modifiers {
  top?: boolean
  right?: boolean
  bottom?: boolean
  left?: boolean
  icon?: boolean
}

function createArrowElement() {
  const arrowEl = document.createElement("div")
  arrowEl.classList.add("absolute", "h-4", "w-4", "rotate-45", "bg-dark")

  return arrowEl
}

function createIconElement() {
  const SVGContainer = document.createElement("div")
  SVGContainer.innerHTML = InfoIcon
  const svg = SVGContainer.firstElementChild as Element
  svg.classList.add("w-4", "h-4", "shrink-0")
  return svg
}

function getPlacementModifier(modifiers: Modifiers): Placement | undefined {
  return Object.keys(modifiers).filter((key) =>
    ["top", "right", "bottom", "left"].includes(key)
  )[0] as Placement | undefined
}

function hasIcon(modifiers: Modifiers): boolean {
  return "icon" in modifiers !== undefined && !!modifiers?.icon
}

const tooltips = new Map<HTMLElement, HTMLElement | undefined>()

function addTooltip(el: HTMLElement, { value, modifiers }: DirectiveBinding) {
  // Add aria-label to tooltip activator element if one doesn't exist.
  // This can be overridden by adding `aria-label` along side `v-tooltip`
  if (!el.getAttribute("aria-label")) {
    el.setAttribute("aria-label", value)
  }

  const modifiedPlacement = getPlacementModifier(modifiers)
  const icon = hasIcon(modifiers)

  const tooltipEl = createTooltipElement(value)
  const arrowEl = createArrowElement()
  if (icon) {
    const iconEl = createIconElement()
    tooltipEl.prepend(iconEl)
  }

  tooltipEl.appendChild(arrowEl)

  tooltips.set(el, document.body.appendChild(tooltipEl))

  const allowedPlacements: Placement[] = ["top", "bottom"]

  const autoPlacementOption: AutoPlacementOptions = {
    alignment: "start",
    autoAlignment: true,
    allowedPlacements,
  }

  function arrowPosition(
    arrow: Partial<Coords>,
    placement: string
  ): { top?: string; right?: string; bottom?: string; left?: string } {
    const { x, y } = arrow

    if (placement === "left") {
      return {
        top: `${y}px`,
        right: `${-arrowEl.offsetWidth / 2}px`,
      }
    }

    if (placement === "right") {
      return {
        top: `${y}px`,
        left: `${-arrowEl.offsetWidth / 2}px`,
      }
    }

    if (placement === "top") {
      return {
        bottom: `${-arrowEl.offsetHeight / 2}px`,
        left: `${x}px`,
      }
    }

    // Default bottom placement
    return {
      top: `${-arrowEl.offsetHeight / 2}px`,
      left: `${x}px`,
    }
  }

  // Compute menu position so the menu is positioned corrected on first load.
  window.requestAnimationFrame(() => {
    computeMenuPosition()
  })

  function computeMenuPosition() {
    const middleware = [
      shift(),
      offset(10),
      arrow({ element: arrowEl as HTMLElement }),
    ]

    if (modifiedPlacement === undefined) {
      middleware.unshift(autoPlacement(autoPlacementOption))
    }
    computePosition(el, tooltipEl as HTMLElement, {
      placement: modifiedPlacement,
      middleware,
    }).then((style) => {
      applyStyles(tooltipEl as HTMLElement, style)
      if (arrowEl && style.middlewareData.arrow) {
        Object.assign(
          arrowEl.style,
          arrowPosition(style.middlewareData.arrow, style.placement)
        )
      }
    })
  }

  el.addEventListener(
    "pointerenter",
    () => {
      tooltipEl.classList.remove("hidden")
      computeMenuPosition()
    },
    false
  )

  el.addEventListener(
    "pointerleave",
    () => {
      tooltipEl.classList.add("hidden")
    },
    false
  )
}

function removeTooltips(el: HTMLElement) {
  const tooltip = tooltips.get(el)
  if (!tooltip) {
    return
  }

  document.body.removeChild(tooltip)
  tooltips.set(el, undefined)
}

function applyStyles(el: FloatingElement, style: ComputePositionReturn) {
  if (!el) {
    return
  }

  Object.assign(el.style, {
    position: style.strategy,
    left: `${style.x}px`,
    top: `${style.y}px`,
  })
}

export const Tooltip: Directive = {
  mounted(el, binding) {
    if (!binding.value) {
      return
    }

    addTooltip(el, binding)
  },
  updated(el, binding) {
    removeTooltips(el)

    if (!binding.value) {
      return
    }

    addTooltip(el, binding)
  },
  unmounted(el) {
    removeTooltips(el)
  },
}
